import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle, CreditCard, Clock } from "@phosphor-icons/react";
import GcrLogo from "../components/GcrLogo";
import apiClient from "../lib/api";
import { BRAND } from "../lib/brand";

export default function TrialSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | ready | maybe_active
  const [errorMsg, setErrorMsg] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [weeklyPrice, setWeeklyPrice] = useState("50");
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/public/pricing`).then(r => r.json()).then(d => {
      if (d.weekly) setWeeklyPrice(String(d.weekly));
    }).catch(() => {});
  }, []);

  // Primary reliable activation: call backend to verify Stripe session directly
  // (does NOT depend on webhook delivery).
  useEffect(() => {
    if (!sessionId) {
      setStatus("maybe_active");
      setErrorMsg("Falta el ID de sesión. Inicia sesión para verificar tu cuenta.");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const MAX = 15;          // 15 × 2s = 30s of retries (webhooks can be slow)
    const DELAY = 2000;

    const attempt = async () => {
      attempts++;
      try {
        const { data } = await apiClient.post("/trial/verify-session", { session_id: sessionId });
        if (cancelled) return;
        if (data?.ok) {
          setUserEmail(data.email || "");
          setStatus("ready");
          return;
        }
        // session not complete yet (Stripe redirects can arrive before the session finalizes)
        if (attempts < MAX) {
          setTimeout(attempt, DELAY);
        } else {
          // Webhook usually completes activation independently. Tell the user their
          // account might already be active and to log in to confirm.
          setStatus("maybe_active");
        }
      } catch (err) {
        if (cancelled) return;
        if (attempts < MAX) {
          setTimeout(attempt, DELAY);
        } else {
          setStatus("maybe_active");
        }
      }
    };
    attempt();
    return () => { cancelled = true; };
  }, [sessionId]);

  const isMaybeActive = status === "maybe_active";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-white px-4" style={{ background: "#050508" }}>
      <Link to="/" className="absolute top-8 left-8" data-testid="trial-success-home">
        <GcrLogo size={56} />
      </Link>
      <div className="max-w-md w-full text-center" data-testid="trial-success-page">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
             style={{
               background: isMaybeActive ? "rgba(212,175,55,0.15)" : "rgba(16,185,129,0.15)",
               border: isMaybeActive ? "2px solid rgba(212,175,55,0.4)" : "2px solid rgba(16,185,129,0.4)",
             }}>
          {isMaybeActive
            ? <CheckCircle weight="duotone" className="w-12 h-12" style={{ color: "#D4AF37" }} data-testid="trial-success-maybe-icon" />
            : <CheckCircle weight="duotone" className="w-12 h-12" style={{ color: "#10B981" }} data-testid="trial-success-ok-icon" />}
        </div>

        {status === "verifying" && (
          <>
            <div className="text-[10px] tracking-[0.3em] uppercase font-black mb-3" style={{ color: "#D4AF37" }}>
              Verificando con Stripe...
            </div>
            <h1 className="font-sport font-black mb-4" style={{ fontSize: "clamp(1.8rem, 5vw, 2.6rem)", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
              Activando tu trial
            </h1>
            <p className="text-sm mb-6" style={{ color: "#9CA3AF", lineHeight: 1.6 }} data-testid="trial-success-verifying-msg">
              Solo unos segundos, no cierres la ventana.
            </p>
          </>
        )}

        {status === "ready" && (
          <>
            <div className="text-[10px] tracking-[0.3em] uppercase font-black mb-3" style={{ color: "#10B981" }}>
              Trial activado
            </div>
            <h1 className="font-sport font-black mb-4" style={{ fontSize: "clamp(2rem, 5vw, 3rem)", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
              ¡Bienvenido a<br />{BRAND.name}!
            </h1>
            <p className="text-sm mb-6" style={{ color: "#9CA3AF", lineHeight: 1.6 }}>
              Tu tarjeta fue verificada. Tienes <strong style={{ color: "#D4AF37" }}>1 día gratis</strong> de
              acceso completo a todos los picks y análisis.
            </p>
            {userEmail && (
              <p className="text-xs mb-4" style={{ color: "#D4AF37" }} data-testid="trial-success-email">
                Cuenta: <strong>{userEmail}</strong>
              </p>
            )}
            <div className="rounded-xl p-4 mb-6 text-left text-xs space-y-2"
                 style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,175,55,0.2)" }}>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#D4AF37" }} />
                <span style={{ color: "#9CA3AF" }}>
                  Tu tarjeta no ha sido cobrada. Al finalizar las 24 horas se cobrarán <strong style={{ color: "#fff" }}>{`$${weeklyPrice}/semana`}</strong> automáticamente.
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CreditCard className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#D4AF37" }} />
                <span style={{ color: "#9CA3AF" }}>
                  Puedes cancelar cuando quieras desde tu perfil sin penalización.
                </span>
              </div>
            </div>
            <button onClick={() => nav("/login")}
                    className="btn-gold w-full py-4 text-sm tracking-widest uppercase font-black"
                    data-testid="trial-success-login">
              Iniciar sesión y empezar →
            </button>
            <p className="text-[11px] mt-4" style={{ color: "#8E8E99" }}>
              Recibirás un email de confirmación en unos minutos.
            </p>
          </>
        )}

        {status === "maybe_active" && (
          <>
            <div className="text-[10px] tracking-[0.3em] uppercase font-black mb-3" style={{ color: "#D4AF37" }}>
              Casi listo
            </div>
            <h1 className="font-sport font-black mb-4" style={{ fontSize: "clamp(2rem, 5vw, 2.8rem)", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
              ¡Pago recibido!
            </h1>
            <p className="text-sm mb-6" style={{ color: "#9CA3AF", lineHeight: 1.6 }} data-testid="trial-success-maybe-msg">
              Tu trial puede tardar unos segundos en activarse. Inicia sesión para confirmar — si aún no aparece premium, recarga la app.
            </p>
            <div className="rounded-xl p-4 mb-6 text-left text-xs space-y-2"
                 style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,175,55,0.2)" }}>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#D4AF37" }} />
                <span style={{ color: "#9CA3AF" }}>
                  Tienes <strong style={{ color: "#fff" }}>1 día gratis</strong>. Tu tarjeta no fue cobrada — pasadas 24h se cobra automáticamente {`$${weeklyPrice}/semana`}.
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CreditCard className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#D4AF37" }} />
                <span style={{ color: "#9CA3AF" }}>
                  Cancela cuando quieras desde tu perfil sin penalización.
                </span>
              </div>
            </div>
            <button onClick={() => nav("/login")}
                    className="btn-gold w-full py-4 text-sm tracking-widest uppercase font-black"
                    data-testid="trial-success-login-fallback">
              Iniciar sesión →
            </button>
            {errorMsg && (
              <p className="text-[11px] mt-4" style={{ color: "#8E8E99" }}>
                {errorMsg}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
